import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import {
  Button,
  Flex,
  Text,
  Button as MantineButton,
} from "@mantine/core";
import { useMediaQuery } from '@mantine/hooks';
import { useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { ReactComponent as IconError } from "src/assets/icons/toast-error.svg";
import { ReactComponent as IconSuccess } from "src/assets/icons/toast-success.svg";
import { ScreenLoader } from "src/components/screenLoader"
import openUpiImage from 'src/assets/images/openUpi.png'
import upiApplications from "src/constants/pennyDropFlow";
import { IconChevronLeft } from "@tabler/icons-react";
import useCleverTapEvents from "src/events/useGetEvents";
import { BANK_DETAILS_EVENT } from "src/events/constant";
import { OnboardingProgress } from "../../components/onboardingProgress";
import { getProgressStepper } from "../../utils/helpers";
import { ScreenWrapper } from "../../components";
import { useStyles } from "./linkBankAccount.styles";
import { RootState } from "../../redux/store";
import { requestRevPennyDrop, getRevPennyDropStatus } from "./bankDetailsSlice";

const API_CALL_INTERVAL = 2000;
const TIMER = 300;
interface IPennyDropFlowProps {
  handleSelectFlow: (flow: 'pennyDrop' | 'manual' | 'null') => void
}

const PennyDropFlow = ({ handleSelectFlow }: IPennyDropFlowProps) => {
  const { classes, cx } = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { TriggerEvent } = useCleverTapEvents()
  const [timer, setTimer] = useState(TIMER);
  const [count, setCount] = useState(0)
  const isMobileDevice = useMediaQuery('(max-width: 500px)');

  const userDetails = useSelector(
    (state: RootState) => state.account.userDetails
  );
  const bankDetailsState = useSelector((state: RootState) => state.bankDetailsData)
  const { data: revPennyDropData, loading: revPennyDropDataLoading } = bankDetailsState.revPennyDrop

  const isDataAvailable = count === 1 && revPennyDropData

  const startRevPennyDrop = () => {
    dispatch(requestRevPennyDrop())
  }

  useEffect(() => {
    startRevPennyDrop()
  }, [])

  useEffect(() => {
    if (bankDetailsState.revPennyDrop.status === 200) {
      // open()
      setCount(1)
    }
  }, [bankDetailsState.revPennyDrop.status])

  // Timer for 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (timer > 0) {
        setTimer((timerValue) => (timerValue > 0 ? timerValue - 1 : 0));
      } else {
        clearInterval(interval);
        handleSelectFlow('null')
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [timer]);

  useEffect(() => {
    const bankDetailsData = bankDetailsState.revPennyDropStatus.data
    const interval = setInterval(() => {
      if (bankDetailsData.status === 'success') {
        notifications.show({
          color: 'green',
          message: 'Bank account verified successfully',
          icon: <IconSuccess />,
        })
        TriggerEvent(BANK_DETAILS_EVENT.bankDetailsSetu, {})
        clearInterval(interval);
        navigate("/verify-image");
      } else if (bankDetailsData && bankDetailsData.status === 'failure') {
        notifications.show({
          color: 'red',
          message: 'Bank account verification failed, Try again!',
          icon: <IconError />,
          autoClose: 1000,
        })
        handleSelectFlow('null')
        // startRevPennyDrop()
      } else {
        dispatch(getRevPennyDropStatus())
      }
    }, API_CALL_INTERVAL);
    return () => {
      clearInterval(interval);
    };
  }, [bankDetailsState.revPennyDropStatus]);

  useEffect(() => {
    if (bankDetailsState.revPennyDropStatus.isError) {
      notifications.update({
        id: userDetails.name,
        color: 'red',
        message: bankDetailsState.revPennyDropStatus.errMessage,
        icon: <IconError />,
      })
      // close()
    }
  }, [bankDetailsState.revPennyDropStatus.isError]);

  function getOperatingSystem() {
    const { userAgent } = window.navigator;
    if (/android/i.test(userAgent)) {
      return "android";
      // @ts-ignore
    } if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return "ios";
    }
    return "Unknown";
  }

  function modifyIntentLink(intentLink: string, appName: string) {
    if (intentLink.startsWith("upi://")) {
      return intentLink.replace("upi://", `${appName}://upi/`);
    }
    return intentLink;
  }

  const renderUpiButton = () => {
    const operatingSystem = getOperatingSystem();
    if (operatingSystem === "ios") {
      return (
        <>
          <Text mb={8} className="content_md_semibold">Select your primary UPI account </Text>
          <Flex w="100%" py="md" justify="space-between">
            {upiApplications.map((app) => (
              <Button
                className={classes.iphoneUpiButton}
                onClick={() => window.open(modifyIntentLink(revPennyDropData.intentLink, app.key))}
              >
                <img src={app.path} alt={app.key} />
              </Button>
            ))}
          </Flex>

        </>
      );
    }
    return (
      <Button
        radius={8}
        fullWidth
        mt={8}
        onClick={() => window.open(`${revPennyDropData.intentLink}`)}
      >
        Open UPI app
      </Button>
    );
  }

  return (
    <ScreenWrapper>
      <div className="csMainContainer">
        <div className="csHeader">
          <div className="csProgressWrapper">
            <OnboardingProgress progress={getProgressStepper(userDetails.currentStep)} />
          </div>
        </div>
        <div className={classes.contentContainer}>
          <h2 className="csHeading">Scan QR Code to Verify Bank</h2>
          <p className="csDescription">
            Pay ₹1 from your bank account you wish to link for trading using
            UPI. We will refund the amount in 2 business days.
          </p>
          {isDataAvailable && (
            <div className={classes.setuFlowContent}>
              {isMobileDevice ? (
                <div className={classes.mobileViewWrapper}>
                  <img
                    className={classes.openUpiImage}
                    src={openUpiImage}
                    alt="images"
                  />
                  {renderUpiButton()}
                </div>
              ) : (
                <div>
                  <img
                    className={classes.qrCode}
                    src={`data:image/jpeg;base64,${revPennyDropData.qrCode}`}
                    alt="QR Code"
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div className={cx("csFooter", classes.setuFooter)}>
          <MantineButton
            radius="md"
            className="heading_2xs_semibold"
            variant="outline"
            onClick={() => handleSelectFlow("null")}
            leftIcon={<IconChevronLeft />}
          >
            Back
          </MantineButton>
        </div>
      </div>
      {revPennyDropDataLoading ? <ScreenLoader /> : null}
    </ScreenWrapper>
  );
}

export default PennyDropFlow
